/**
 * The M2 HUD feature (plan §6 M2-19), order `FEATURE_ORDER.m2Hud`: after the M1 HUD (which built `#hud`'s markup
 * at its own init), so the M2 layer is appended beside it. Career mode only; a no-op in sandbox. Per frame: refill
 * the camera and source structs in place, run the pure model (src/hud/m2/model.ts), then the diffed view
 * (src/hud/m2/view.ts). While a UI screen is up the view hides the whole HUD (`#hud[data-ui="open"]`). The viewport
 * size is cached from resize events, never read per frame. Allocation-free per frame (D-014).
 */
import type { FrameInfo, GameContext, GameFeature } from '../../app/feature'
import { FEATURE_ORDER } from '../../app/feature'
import '../../hud/m2/m2hud.css'
import { createM2HudState, updateM2HudModel, type M2HudSource, type M2HudState } from '../../hud/m2/model'
import { createM2HudView, mountM2Hud, renderM2Hud, type M2HudView } from '../../hud/m2/view'
import { buildBoomHudElements, createBoomHud, createBoomHudView, renderBoomHud, updateBoomHud, type BoomHud, type BoomHudView } from '../../hud/boomMarks'
import { createHudCamera, setHudCamera, type HudCamera } from '../../hud/projection'
import { loadReachHudConfig } from '../../hud/reach/config'
import { updatePadActive } from '../../hud/reach/model'
import type { M2Runtime } from '../../space/contracts'
import { cruiseChargeSFor, cruiseLagSFor, loadTravelConfig, type TravelConfig } from '../../travel/config'
import { bandOfShip } from '../../travel/runtime/flight'
import { getM2 } from '../../space/world'
import { Life, Pos64, Target } from '../../world/components'
import { m2HudTuning } from './tuning'

let view: M2HudView | null = null
let state: M2HudState | null = null
let src: M2HudSource | null = null
let m2: M2Runtime | null = null
let boomHud: BoomHud | null = null
let boomView: BoomHudView | null = null
const cam: HudCamera = createHudCamera()
let width = 1
let height = 1
/** The pad is the active device (the dock prompt's glyph, CL-2): the Reach HUD's rule and threshold. */
let padActive = false
const padAxisThreshold = loadReachHudConfig().device.padAxisThreshold
/** reach-travel.json, read once: the far station markers' cruise times (CL-8) use the cruise law the drive flies. */
const travelConfig: TravelConfig = loadTravelConfig()

function readViewport(): void {
  width = window.innerWidth
  height = window.innerHeight
}

/** Refills the source from the runtime and the sim. Allocation-free. */
export function fillM2HudSource(out: M2HudSource, runtime: M2Runtime, playerEid: number, f: FrameInfo, dockKey: string, pad: boolean, travel: TravelConfig | null = null): void {
  const ui = runtime.ui.getState()
  out.space = runtime.space
  out.career = ui.career
  out.uiOpen = ui.screen !== 'none'
  out.playerEid = playerEid
  out.alive = playerEid >= 0 && Life.alive[playerEid] === 1
  if (playerEid >= 0) {
    out.px = Pos64.x[playerEid]
    out.py = Pos64.y[playerEid]
    out.pz = Pos64.z[playerEid]
  }
  // The combat target (its edge arrow keeps the station labels clear; W1 polish).
  const t = playerEid >= 0 ? Target.eid[playerEid] : -1
  out.targetOn = t >= 0 && t !== playerEid && Life.alive[t] === 1
  if (out.targetOn) {
    out.tx = Pos64.x[t]
    out.ty = Pos64.y[t]
    out.tz = Pos64.z[t]
  }
  out.dt = f.dt
  out.paused = f.paused
  out.dockKey = dockKey
  out.padActive = pad
  // CL-8: cruise times only where the drive exists (the Reach), led in by the flown hull's charge and spin-up.
  out.travel = travel
  if (travel !== null) {
    const band = playerEid >= 0 ? bandOfShip(runtime.space.sim, playerEid) : null
    out.cruiseLeadS = cruiseChargeSFor(travel, band) + cruiseLagSFor(travel, band)
  } else out.cruiseLeadS = 0
}

/**
 * Mounts the HUD once the career feature has provided the `m2` service (at its init, or later when a career
 * starts). Runs once; allocates only then.
 */
function attach(ctx: GameContext, runtime: M2Runtime): void {
  m2 = runtime
  // The station list follows the Reach system (M3 R22e): the markers cover the most stations a system can have.
  const stations = Math.max(runtime.space.stations.list.length, m2HudTuning.maxStationMarkers)
  const counts = { stations, contracts: m2HudTuning.maxContractMarkers, toasts: m2HudTuning.toasts.max }
  view = createM2HudView(mountM2Hud(ctx.hudRoot, counts, m2HudTuning))
  state = createM2HudState(stations, counts.contracts, counts.toasts)
  // P5a12c: the boom HUD mounts its elements on the first boom-hull frame (an S/M HUD never gets them).
  const host = ctx.hudRoot
  boomHud = createBoomHud()
  boomView = createBoomHudView(() =>
    buildBoomHudElements(host, (cls, parent) => {
      const d = document.createElement('div')
      d.className = cls
      parent.appendChild(d)
      return d
    }),
  )
  src = {
    space: runtime.space,
    career: null,
    uiOpen: true,
    playerEid: -1,
    alive: false,
    px: 0,
    py: 0,
    pz: 0,
    cam,
    targetOn: false,
    tx: 0,
    ty: 0,
    tz: 0,
    dt: 0,
    paused: true,
    dockKey: '',
    padActive: false,
    travel: null,
    cruiseLeadS: 0,
  }
}

/** One frame of the M2 HUD (career mode only). Allocation-free while values are steady. */
export function frameM2Hud(ctx: GameContext, f: FrameInfo): void {
  if (ctx.mode !== 'career') return
  if (!m2) {
    const runtime = getM2(ctx)
    if (!runtime) return
    attach(ctx, runtime)
  }
  if (!view || !state || !src || !m2) return
  const camera = ctx.camera
  setHudCamera(cam, ctx.rig.pos, camera.position, camera.quaternion, camera.fov, camera.zoom, width, height)
  padActive = updatePadActive(padActive, ctx.input, padAxisThreshold)
  fillM2HudSource(src, m2, ctx.sim.playerEid, f, ctx.data.bindings.keyboard.dock[0] ?? '', padActive, ctx.reach !== null ? travelConfig : null)
  updateM2HudModel(state, src, m2HudTuning)
  renderM2Hud(view, state, m2HudTuning)
  if (boomHud && boomView) {
    updateBoomHud(boomHud, m2.space.rocks.readout.booms, cam, m2HudTuning.boomRing)
    renderBoomHud(boomView, boomHud)
  }
}

const feature: GameFeature = {
  name: 'm2hud',
  order: FEATURE_ORDER.m2Hud,
  init(ctx: GameContext): void {
    if (ctx.mode !== 'career') return
    // The career feature (order 150) usually provides `m2` at its init; otherwise the first frame after it does.
    const runtime = getM2(ctx)
    if (runtime) attach(ctx, runtime)
    readViewport()
    window.addEventListener('resize', readViewport)
  },
  frame: frameM2Hud,
  dispose(): void {
    window.removeEventListener('resize', readViewport)
    view = null
    state = null
    src = null
    m2 = null
    boomHud = null
    boomView = null
    padActive = false
  },
}

export default feature
